/**
 * Indeterminate loading indicators.
 *
 * `Spinner` announces itself through a visually hidden status message; pass
 * `decorative` when the surrounding element already says what is loading (a
 * button label, or the overlay below) so the message is not read out twice.
 */
import { forwardRef, type HTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<SpinnerSize, string> = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-8 w-8',
};

export interface SpinnerProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  size?: SpinnerSize;
  label?: string;
  /** Renders the icon only — no role, no announcement. */
  decorative?: boolean;
}

export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner(
  { className, size = 'md', label = 'Loading…', decorative = false, ...props },
  ref,
) {
  return (
    <span
      ref={ref}
      role={decorative ? undefined : 'status'}
      aria-hidden={decorative ? true : undefined}
      className={cn('inline-flex items-center justify-center text-content-muted', className)}
      {...props}
    >
      <Loader2 className={cn('animate-spin', SIZE_CLASS[size])} aria-hidden="true" />
      {decorative ? null : <span className="sr-only">{label}</span>}
    </span>
  );
});

export interface LoadingOverlayProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Announced to screen readers, and shown as text when `showLabel` is set. */
  label?: string;
  showLabel?: boolean;
  size?: SpinnerSize;
}

/**
 * Covers its nearest positioned ancestor while a section refetches, keeping the
 * stale content visible underneath instead of collapsing the layout.
 */
export const LoadingOverlay = forwardRef<HTMLDivElement, LoadingOverlayProps>(function LoadingOverlay(
  { className, label = 'Loading…', showLabel = false, size = 'md', ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        'absolute inset-0 z-10 flex animate-fade-in flex-col items-center justify-center gap-2',
        'rounded-[inherit] bg-surface/75 backdrop-blur-[1px]',
        className,
      )}
      {...props}
    >
      <Spinner size={size} decorative className="text-primary" />
      <span className={showLabel ? 'text-xs font-medium text-content-muted' : 'sr-only'}>{label}</span>
    </div>
  );
});
